import { useEffect, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Project } from '@vd/types';
import { api } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import styles from './ProjectMark.module.css';

/**
 * A project's mark (16 Oct): its icon, the site's own or one uploaded
 * (apps/api/src/projectIcons.ts), else a rounded square of its colour with
 * its initials. One letter below 20px, two from 20px. Anything that names a
 * project shows this instead of a dot.
 *
 * The icon comes from the workspace's project list, which the sidebar has
 * already read, so a block or a report row that knows only a project's id,
 * name and colour still shows it; `iconUrl` passed in wins. A picture that
 * fails to load falls back to the initials.
 */
export function ProjectMark({ project, size = 16, className }: {
  project: { id: string; name: string; color: string; iconUrl?: string | null };
  size?: number;
  className?: string;
}) {
  const listed = useProjectIconUrl(project.id, project.iconUrl === undefined);
  const url = project.iconUrl !== undefined ? project.iconUrl : listed;
  return <Mark name={project.name} color={project.color} url={url} size={size} className={className} testId="project-mark" />;
}

/**
 * The picture, or the rounded square of a colour with initials when there is
 * none or it fails to load: a project's mark and a workspace's (TOADO-906).
 */
export function Mark({ name, color, url, size = 16, className, testId }: {
  name: string;
  color: string;
  url: string | null;
  size?: number;
  className?: string;
  testId: string;
}) {
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => setFailed(null), [url]);
  const style = { '--mark-size': `${size}px`, '--mark-color': color, '--mark-ink': inkOn(color) } as CSSProperties;
  const classes = [styles.mark, className].filter(Boolean).join(' ');
  if (url && failed !== url) {
    return <img className={classes} style={style} src={url} alt="" width={size} height={size} loading="lazy" onError={() => setFailed(url)} data-testid={testId} data-kind="icon" />;
  }
  return (
    <span className={classes} style={style} aria-hidden="true" data-testid={testId} data-kind="initials">
      {projectInitials(name, size >= 20 ? 2 : 1)}
    </span>
  );
}

/** The icon link the workspace's project list gives a project, read from the list the sidebar keeps. */
function useProjectIconUrl(projectId: string, wanted: boolean): string | null {
  const { currentCompanyId } = useAuth();
  const { data } = useQuery({
    queryKey: ['projects', currentCompanyId],
    queryFn: () => (currentCompanyId ? api.listProjects(currentCompanyId) : Promise.resolve([] as Project[])),
    enabled: wanted && !!currentCompanyId,
  });
  return data?.find((project) => project.id === projectId)?.iconUrl ?? null;
}

/**
 * A project's initials: the first letters of its first two words, or the
 * first two of a single word, as many as `count` asks for. Letters and
 * digits only, so "=Marketing site" is "MS" and an emoji is passed over.
 */
export function projectInitials(name: string, count: 1 | 2 = 2): string {
  const words = name.normalize('NFC').split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  if (words.length === 0) return '?';
  const letters = words.length >= 2 ? [words[0]!, words[1]!].map((word) => Array.from(word)[0]!) : Array.from(words[0]!).slice(0, 2);
  return letters.slice(0, count).join('').toLocaleUpperCase();
}

/** White or near-black, whichever reads better on a colour (WCAG relative luminance). */
export function inkOn(color: string): string {
  const hex = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim())?.[1];
  if (!hex) return '#ffffff';
  const full = hex.length === 3 ? hex.split('').map((digit) => digit + digit).join('') : hex;
  const [r, g, b] = [0, 2, 4].map((at) => {
    const channel = parseInt(full.slice(at, at + 2), 16) / 255;
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
  // Contrast against white is (1.05) / (L + 0.05); against #09090B about (L + 0.05) / 0.053.
  return 1.05 / (luminance + 0.05) >= (luminance + 0.05) / 0.053 ? '#ffffff' : '#09090b';
}
